import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AdminSession {
  accessToken: string;
  refreshToken: string;
  expiresInSec: number;
  actorType: 'ADMIN';
  role: string;
  scope: string[];
  /** ISO timestamp at which the access token expires (client clock). */
  expiresAt: string;
}

interface AuthState {
  session: AdminSession | null;
  setSession: (session: AdminSession | null) => void;
  hasScope: (key: string) => boolean;
  hasAnyRole: (...roles: string[]) => boolean;
  refresh: () => Promise<boolean>;
  logout: (redirectToLogin?: boolean) => Promise<void>;
}

// Imported lazily inside thunks to avoid the circular dep between the axios
// client (which uses this store) and the auth service (which uses the client).
async function loadAuthService() {
  const mod = await import('../services/auth');
  return mod.authService;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      setSession: (session) => set({ session }),
      hasScope: (key) => {
        const session = get().session;
        if (!session) return false;
        if (session.role === 'SUPER_ADMIN') return true;
        return session.scope.includes(key);
      },
      hasAnyRole: (...roles) => {
        const session = get().session;
        if (!session) return false;
        return roles.includes(session.role);
      },
      refresh: async () => {
        const session = get().session;
        if (!session) return false;
        try {
          const svc = await loadAuthService();
          const next = await svc.refresh(session.refreshToken);
          set({ session: next });
          return true;
        } catch {
          set({ session: null });
          return false;
        }
      },
      logout: async (redirectToLogin = true) => {
        const session = get().session;
        if (session) {
          try {
            const svc = await loadAuthService();
            await svc.logout(session.accessToken);
          } catch {
            // Ignored — backend logout is best-effort; we always clear locally.
          }
        }
        set({ session: null });
        if (redirectToLogin && typeof window !== 'undefined') {
          window.location.assign('/login');
        }
      },
    }),
    {
      name: 'utsaha-admin-session',
      // Only persist the session — function refs would be discarded anyway.
      partialize: (state) => ({ session: state.session }),
    },
  ),
);
